import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, ExternalLink, Info, RefreshCw, Trash2 } from 'lucide-react';
import { APP_CONFIG } from '@/config/app.config';
import { useAppCache } from '@/contexts/AppCacheContext';

interface DomainActionsDropdownProps {
  domain: {
    id: string;
    domain: string;
    type: 'subdomain' | 'custom';
    verified: boolean;
  };
  // Callbacks — pass undefined to hide the action
  onShowRecords?: () => void;
  onVerify?: () => void;
  onDelete?: () => void;
  // Options
  triggerVariant?: 'icon' | 'button';
  // Loading states
  isVerifying?: boolean;
  isDeleting?: boolean;
}

/**
 * Reusable dropdown menu for domain actions.
 * Designed for the domains table row and any future domain detail page.
 */
export const DomainActionsDropdown: React.FC<DomainActionsDropdownProps> = ({
  domain,
  onShowRecords,
  onVerify,
  onDelete,
  triggerVariant = 'icon',
  isVerifying = false,
  isDeleting = false,
}) => {
  const { hasPermission } = useAppCache();
  const canUpdate = hasPermission('update:domains');
  const canDelete = hasPermission('delete:domains');

  const isCustom = domain.type === 'custom';
  const isSubdomain = domain.type === 'subdomain';

  const href = isSubdomain
    ? `https://${domain.domain}.${APP_CONFIG.SITES_DOMAIN}`
    : `https://${domain.domain}`;
  const showVisit = isSubdomain || domain.verified;

  const showRecords = isCustom && !domain.verified && !!onShowRecords;
  const showVerify = isCustom && !domain.verified && !!onVerify && canUpdate;
  const showDelete = isCustom && !!onDelete && canDelete;

  // Nothing to show → render nothing so the cell stays clean.
  if (!showVisit && !showRecords && !showVerify && !showDelete) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {triggerVariant === 'icon' ? (
          <Button size="icon" variant="tertiary" title="More actions" className="h-8 w-8 hover:no-underline">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        ) : (
          <Button size="default" variant="primary">
            <MoreHorizontal />
            Actions
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {showVisit && (
          <DropdownMenuItem asChild>
            <a href={href} target="_blank" rel="noopener noreferrer">
              <ExternalLink />
              Visit domain
            </a>
          </DropdownMenuItem>
        )}

        {showRecords && (
          <DropdownMenuItem onClick={onShowRecords}>
            <Info />
            Records
          </DropdownMenuItem>
        )}

        {showVerify && (
          <DropdownMenuItem onClick={onVerify} disabled={isVerifying}>
            <RefreshCw className={isVerifying ? 'animate-spin' : undefined} />
            {isVerifying ? 'Verifying…' : 'Verify'}
          </DropdownMenuItem>
        )}

        {showDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onDelete}
              disabled={isDeleting}
              className="text-red-400 data-[highlighted]:bg-red-500/15 data-[highlighted]:text-red-300"
            >
              <Trash2 />
              {isDeleting ? 'Deleting…' : 'Delete domain'}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default DomainActionsDropdown;
